import axios from 'axios';
import { appConfig } from '@/config';
import { STORAGE_KEYS } from '@/constants';

/**
 * Centralized Axios client.
 *
 * Refresh strategy:
 * - On 401, attempt a single refresh via POST /auth/refresh (credentials: include)
 * - Queue concurrent requests while refresh is in-flight (avoid parallel refresh storms)
 * - Cross-tab single-flight via navigator.locks + BroadcastChannel token sync
 * - Skip refresh for auth endpoints themselves to prevent infinite loops
 * - Logout only on definitive session errors (401/403 from refresh), not network/429/5xx
 *
 * Do not call Axios directly from components — use feature services + hooks.
 */

const REFRESH_LOCK = '3la-auth-refresh';
const AUTH_CHANNEL = '3la-auth';
const REFRESH_RETRY_DELAY_MS = 250;
const SESSION_DEAD_CODES = new Set([
  'REFRESH_INVALID',
  'REFRESH_REQUIRED',
  'UNAUTHORIZED',
  'TOKEN_INVALID',
]);

const api = axios.create({
  baseURL: appConfig.apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

let accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || null;
let isRefreshing = false;
let refreshQueue = [];
let authChannel = null;

export function setAccessToken(token, { broadcast = true } = {}) {
  accessToken = token;
  if (token) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  if (broadcast) {
    getAuthChannel()?.postMessage({
      type: token ? 'token' : 'logout',
      token: token || null,
    });
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('auth:token', {
        detail: { token: token || null },
      }),
    );
  }
}

export function getAccessToken() {
  return accessToken;
}

function getAuthChannel() {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!authChannel) {
    authChannel = new BroadcastChannel(AUTH_CHANNEL);
    authChannel.addEventListener('message', (event) => {
      const { type, token } = event.data || {};
      if (type === 'token' && token) {
        accessToken = token;
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
        window.dispatchEvent(new CustomEvent('auth:token', { detail: { token } }));
      }
      if (type === 'logout') {
        accessToken = null;
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        window.dispatchEvent(new CustomEvent('auth:token', { detail: { token: null } }));
      }
    });
  }
  return authChannel;
}

// Eagerly subscribe so other tabs can push tokens before the first 401 here.
getAuthChannel();

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

function isAuthRefreshUrl(url = '') {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/reset-password')
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getErrorCode(error) {
  return error?.response?.data?.error?.code || null;
}

function isSessionDeadError(error) {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return false;
  const code = getErrorCode(error);
  return !code || SESSION_DEAD_CODES.has(code);
}

async function postRefresh() {
  const { data } = await api.post('/auth/refresh', {});
  const newToken = data?.data?.accessToken;
  if (!newToken) {
    throw new Error('Refresh response missing accessToken');
  }
  return newToken;
}

async function refreshAccessToken() {
  const run = async () => {
    try {
      return await postRefresh();
    } catch (error) {
      // Another tab may have just rotated the cookie — retry once before giving up.
      if (getErrorCode(error) === 'REFRESH_INVALID') {
        await sleep(REFRESH_RETRY_DELAY_MS);
        return postRefresh();
      }
      throw error;
    }
  };

  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK, run);
  }
  return run();
}

function forceLogout() {
  setAccessToken(null);
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (isAuthRefreshUrl(originalRequest.url) || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      setAccessToken(newToken);
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      if (isSessionDeadError(refreshError)) {
        forceLogout();
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
